import React, { useMemo, useState } from "react";

import { ChevronRight, ExpandMore } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import clsx from "clsx";

import mainStyles from "../../styles";

export interface FilterTypeItem {
  DisplayName: string;
  Category: string;
  Value: string | number;
  isCategory?: boolean; // true if this represents a category node
  children?: FilterTypeItem[]; // child items for category nodes
  parentCategory?: string; // the category this item belongs to
}

interface FilterTypeAutoCompleteProps {
  label: string;
  items: FilterTypeItem[];
  selectedItems: FilterTypeItem[];
  setSelectedItems: (newShown: FilterTypeItem[]) => void;
  testId?: string;
}

const FilterTypeAutoComplete: React.FC<FilterTypeAutoCompleteProps> = ({
  label,
  items,
  selectedItems,
  setSelectedItems,
  testId,
}) => {
  const classes = mainStyles();

  // Track which categories are expanded (all expanded by default)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    () => {
      const categories = items
        .filter((item) => item.isCategory)
        .map((item) => String(item.Value));
      return new Set(categories);
    },
  );

  // Filter items to only show expanded children
  const visibleItems = useMemo(() => {
    return items.filter((item) => {
      if (item.isCategory) return true; // Always show categories
      // Show children only if their parent category is expanded
      const categoryValue = `category:${item.parentCategory}`;
      return expandedCategories.has(categoryValue);
    });
  }, [items, expandedCategories]);

  const toggleCategory = (categoryValue: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryValue)) {
        next.delete(categoryValue);
      } else {
        next.add(categoryValue);
      }
      return next;
    });
  };

  // Handle selection change with category/child logic
  const handleChange = (_: unknown, newValue: FilterTypeItem[]) => {
    // newValue only contains actual action items (no categories)
    // because displayValue filters them out
    const previousValues = new Set(selectedItems.map((item) => item.Value));
    const newValues = new Set(newValue.map((item) => item.Value));

    // Determine what was added or removed
    const addedItems = newValue.filter(
      (item) => !previousValues.has(item.Value),
    );
    const removedItems = selectedItems.filter(
      (item) => !newValues.has(item.Value),
    );

    let result = [...newValue];

    // Handle added items - check if clicking was on a category checkbox
    addedItems.forEach((addedItem) => {
      if (addedItem.isCategory && addedItem.children) {
        // Category was selected - add all its children
        const childrenNotInResult = addedItem.children.filter(
          (child) => !newValues.has(child.Value),
        );
        result = [...result, ...childrenNotInResult];
      }
    });

    // Handle removed items - check if a category's children should be removed
    removedItems.forEach((removedItem) => {
      if (removedItem.isCategory && removedItem.children) {
        // Category was deselected - remove all its children
        result = result.filter(
          (item) =>
            !removedItem.children?.some((child) => child.Value === item.Value),
        );
      }
    });

    // Only store actual action items, never category items
    const finalResult = result.filter((item) => !item.isCategory);
    setSelectedItems(finalResult);
  };

  // Only show actual action items in the Autocomplete value
  const displayValue = selectedItems;

  return (
    <Grid xs={12}>
      <form
        className={clsx(classes.spaceSearchNoMargin)}
        noValidate
        autoComplete="off"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignContent="space-between"
        >
          <Grid item xs={6}>
            <Autocomplete
              multiple
              size="medium"
              id="combo-box-demo"
              limitTags={2}
              options={visibleItems}
              value={displayValue}
              getOptionLabel={(option) => option.DisplayName}
              getOptionKey={(option) => String(option.Value)}
              onChange={handleChange}
              disableCloseOnSelect
              isOptionEqualToValue={(option, value) =>
                option.Value === value.Value
              }
              renderOption={(props, option, { selected }) => {
                const isCategory = option.isCategory === true;
                const isIndented = !isCategory && option.parentCategory;
                const isExpanded =
                  isCategory && expandedCategories.has(String(option.Value));

                // For category items, check if all children are selected
                let isCategoryFullySelected = false;
                if (isCategory && option.children) {
                  const selectedValues = new Set(
                    selectedItems.map((item) => item.Value),
                  );
                  isCategoryFullySelected = option.children.every((child) =>
                    selectedValues.has(child.Value),
                  );
                }

                // eslint-disable-next-line react/prop-types
                const { style, onClick, ...otherProps } = props;

                const handleLiClick = (e: React.MouseEvent) => {
                  // For categories, prevent default selection behavior
                  if (isCategory) {
                    e.preventDefault();
                    e.stopPropagation();
                  } else {
                    // Call the Autocomplete's onClick handler for child items
                    if (onClick) {
                      onClick(e as React.MouseEvent<HTMLLIElement>);
                    }
                  }
                };

                return (
                  <li
                    {...otherProps}
                    onClick={handleLiClick}
                    style={{
                      ...style,
                      paddingLeft: isIndented ? "48px" : "8px",
                      fontWeight: isCategory ? "bold" : "normal",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {isCategory && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleCategory(String(option.Value));
                        }}
                        style={{ padding: 4, marginRight: 4 }}
                      >
                        {isExpanded ? (
                          <ExpandMore fontSize="small" />
                        ) : (
                          <ChevronRight fontSize="small" />
                        )}
                      </IconButton>
                    )}
                    {!isCategory && (
                      <Box style={{ width: 32, marginRight: 4 }} />
                    )}
                    <Checkbox
                      checked={isCategory ? isCategoryFullySelected : selected}
                      indeterminate={
                        isCategory &&
                        !isCategoryFullySelected &&
                        option.children?.some((child) =>
                          selectedItems.some((s) => s.Value === child.Value),
                        )
                      }
                      onClick={(e) => {
                        if (isCategory && option.children) {
                          // Only prevent default for categories
                          e.preventDefault();
                          e.stopPropagation();

                          // Handle category checkbox click manually
                          if (isCategoryFullySelected) {
                            // Deselect all children
                            const childValues = new Set(
                              option.children.map((c) => c.Value),
                            );
                            setSelectedItems(
                              selectedItems.filter(
                                (item) => !childValues.has(item.Value),
                              ),
                            );
                          } else {
                            // Select all children
                            const childValues = new Set(
                              selectedItems.map((item) => item.Value),
                            );
                            const newChildren = option.children.filter(
                              (child) => !childValues.has(child.Value),
                            );
                            setSelectedItems([
                              ...selectedItems,
                              ...newChildren,
                            ]);
                          }
                        }
                        // For child items, let the click propagate to trigger Autocomplete selection
                      }}
                      style={{ marginRight: 8 }}
                    />
                    <Box
                      onClick={(e) => {
                        if (isCategory && option.Value) {
                          // Clicking on category label toggles expansion
                          e.preventDefault();
                          e.stopPropagation();
                          toggleCategory(String(option.Value));
                        }
                        // For child items, let the click bubble to li element
                      }}
                      style={{ flex: 1, cursor: "pointer" }}
                    >
                      {option.DisplayName}
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label={label} data-testid={testId} />
              )}
            />
          </Grid>
          <Box padding={1}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={() => setSelectedItems([])}
              data-testid="apply-clear-activity-filter"
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
      </form>
    </Grid>
  );
};

export default FilterTypeAutoComplete;
